import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { Skeleton } from "../components/ui/Skeleton";
import { getApiOrigin } from "../api/axiosConfig";
import { clearPublicBrandingCache } from "../api/publicBrandingApi";
import { settingsApi, TenantSettingsDto, UpdateTenantSettingsRequest } from "../api/settingsApi";
import { getApiErrorPayload } from "../utils/apiError";

const DEFAULT_PRIMARY = "#0B1D3A";
const DEFAULT_ACCENT = "#10B981";

function emptyToDto(): TenantSettingsDto {
  return {
    companyName: "",
    businessEmail: "",
    businessPhone: "",
    businessStreet: "",
    businessCity: "",
    businessState: "",
    businessZip: "",
    websiteUrl: "",
    subdomain: "",
    portalSubdomainDisplay: "",
    subscriptionTier: "",
    customDomain: "",
    customDomainStatus: "",
    customDomainEditable: false,
    logoUrl: "",
    faviconUrl: "",
    primaryColor: DEFAULT_PRIMARY,
    accentColor: DEFAULT_ACCENT,
    tagline: "",
    portalWelcomeMessage: "",
    chatbotName: "",
    chatbotWelcomeMessage: "",
    emailFromName: "",
    replyToEmail: "",
    emailFooter: "",
    emailProviderStatus: "",
    notifyEmailDisputeUpdates: true,
    notifyEmailNewClientSignups: true,
    notifyEmailBillingAlerts: true,
    notifyInAppDisputeUpdates: true,
    notifyInAppNewClientSignups: true,
    notifyInAppBillingAlerts: true,
    notifyClientDisputeStatus: true,
    notifyClientScoreUpdates: true,
    clientSelfSignup: false,
    requireDocumentsOnSignup: false,
    defaultPortalLanguage: "en",
    aiChatbotEnabled: true,
    operatorSignerLegalName: "",
    operatorSignerTitle: "",
  };
}

const SettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [form, setForm] = useState<TenantSettingsDto>(() => emptyToDto());
  const [deletePhrase, setDeletePhrase] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await settingsApi.get();
      setForm({
        ...s,
        primaryColor: s.primaryColor || DEFAULT_PRIMARY,
        accentColor: s.accentColor || DEFAULT_ACCENT,
      });
    } catch {
      setError("Could not load settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const flashSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 3000);
  };

  const planKey = useMemo(
    () => (form.subscriptionTier || "Starter").trim().toLowerCase(),
    [form.subscriptionTier]
  );
  const tierIsStarter = planKey === "starter";
  const tierIsAgency = planKey === "agency";

  const save = async (partial?: UpdateTenantSettingsRequest) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body: UpdateTenantSettingsRequest = partial ?? {
        companyName: form.companyName,
        businessEmail: form.businessEmail,
        businessPhone: form.businessPhone,
        businessStreet: form.businessStreet,
        businessCity: form.businessCity,
        businessState: form.businessState,
        businessZip: form.businessZip,
        websiteUrl: form.websiteUrl,
        logoUrl: form.logoUrl,
        faviconUrl: form.faviconUrl,
        primaryColor: form.primaryColor,
        accentColor: form.accentColor,
        tagline: form.tagline,
        portalWelcomeMessage: form.portalWelcomeMessage,
        chatbotName: form.chatbotName,
        chatbotWelcomeMessage: form.chatbotWelcomeMessage,
        customDomain: form.customDomainEditable ? form.customDomain : undefined,
        emailFromName: form.emailFromName,
        replyToEmail: form.replyToEmail,
        emailFooter: form.emailFooter,
        notifyEmailDisputeUpdates: form.notifyEmailDisputeUpdates,
        notifyEmailNewClientSignups: form.notifyEmailNewClientSignups,
        notifyEmailBillingAlerts: form.notifyEmailBillingAlerts,
        notifyInAppDisputeUpdates: form.notifyInAppDisputeUpdates,
        notifyInAppNewClientSignups: form.notifyInAppNewClientSignups,
        notifyInAppBillingAlerts: form.notifyInAppBillingAlerts,
        notifyClientDisputeStatus: form.notifyClientDisputeStatus,
        notifyClientScoreUpdates: form.notifyClientScoreUpdates,
        clientSelfSignup: form.clientSelfSignup,
        requireDocumentsOnSignup: form.requireDocumentsOnSignup,
        defaultPortalLanguage: form.defaultPortalLanguage,
        aiChatbotEnabled: form.aiChatbotEnabled,
      };
      const s = await settingsApi.update(body);
      setForm({
        ...s,
        primaryColor: s.primaryColor || DEFAULT_PRIMARY,
        accentColor: s.accentColor || DEFAULT_ACCENT,
      });
      clearPublicBrandingCache(s.subdomain);
      flashSuccess("Settings saved.");
    } catch (e: unknown) {
      setError(getApiErrorPayload(e)?.message ?? "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingLogo(true);
    setError(null);
    try {
      const url = await settingsApi.uploadLogo(file);
      setForm((p) => ({ ...p, logoUrl: url }));
      clearPublicBrandingCache(form.subdomain);
      flashSuccess("Logo uploaded.");
    } catch {
      setError("Logo upload failed. Use PNG, JPG, or SVG under 2MB.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleFavicon = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (tierIsStarter) {
      e.target.value = "";
      return;
    }
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingFavicon(true);
    setError(null);
    try {
      const url = await settingsApi.uploadFavicon(file);
      setForm((p) => ({ ...p, faviconUrl: url }));
      clearPublicBrandingCache(form.subdomain);
      flashSuccess("Favicon uploaded.");
    } catch (e: unknown) {
      setError(getApiErrorPayload(e)?.message ?? "Favicon upload failed. Use PNG, JPG, or SVG under 2MB.");
    } finally {
      setUploadingFavicon(false);
    }
  };

  const saveContractSigner = async () => {
    const legalName = form.operatorSignerLegalName.trim();
    const title = form.operatorSignerTitle.trim();
    if (legalName.length < 2 || legalName.length > 200) {
      setError("Operator legal name must be 2–200 characters.");
      return;
    }
    if (title.length < 2 || title.length > 150) {
      setError("Operator title must be 2–150 characters.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await settingsApi.updateContractSigner(legalName, title);
      setForm((p) => ({
        ...p,
        operatorSignerLegalName: res.operatorSignerLegalName,
        operatorSignerTitle: res.operatorSignerTitle,
      }));
      flashSuccess("Contract signer saved.");
    } catch (e: unknown) {
      setError(getApiErrorPayload(e)?.message ?? "Could not save contract signer.");
    } finally {
      setSaving(false);
    }
  };

  const exportData = async () => {
    try {
      const blob = await settingsApi.exportJson();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tenant-export-${form.subdomain}.json`;
      a.click();
      URL.revokeObjectURL(url);
      flashSuccess("Export started.");
    } catch {
      setError("Export failed.");
    }
  };

  const requestDelete = async () => {
    if (!deletePhrase.trim()) {
      setError("Type the confirmation phrase to continue.");
      return;
    }
    try {
      await settingsApi.requestDelete(deletePhrase.trim());
      flashSuccess("Deletion request submitted.");
      setDeletePhrase("");
    } catch {
      setError("Could not submit deletion request.");
    }
  };

  const apiOrigin = getApiOrigin();
  const logoPreview =
    form.logoUrl && !form.logoUrl.startsWith("http")
      ? `${apiOrigin}${form.logoUrl.startsWith("/") ? "" : "/"}${form.logoUrl}`
      : form.logoUrl;

  const openPortalPreview = () => {
    const base = window.location.origin;
    const url = `${base}/portal/login?subdomain=${encodeURIComponent(form.subdomain)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-white">Settings</h1>
          <p className="text-sm text-slate-400">
            Business profile, white-label portal branding, domain, email, notifications, and portal behavior.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-800/50 bg-red-950/50 px-3 py-2 text-sm text-red-200">{error}</div>
        )}
        {success && (
          <div className="rounded-lg border border-emerald-800/40 bg-emerald-950/40 px-3 py-2 text-sm text-emerald-100">{success}</div>
        )}

        {/* 2a Business */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-lg font-semibold text-white">Business information</h2>
          <p className="mt-1 text-sm text-slate-400">Details shown on documents and used as defaults for email.</p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-slate-400">Company name</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.companyName}
                onChange={(e) => setForm((p) => ({ ...p, companyName: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-400">Business email</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.businessEmail}
                onChange={(e) => setForm((p) => ({ ...p, businessEmail: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-400">Business phone</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.businessPhone}
                onChange={(e) => setForm((p) => ({ ...p, businessPhone: e.target.value }))}
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-slate-400">Street address</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.businessStreet}
                onChange={(e) => setForm((p) => ({ ...p, businessStreet: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-400">City</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.businessCity}
                onChange={(e) => setForm((p) => ({ ...p, businessCity: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-400">State</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.businessState}
                onChange={(e) => setForm((p) => ({ ...p, businessState: e.target.value }))}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-400">ZIP</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.businessZip}
                onChange={(e) => setForm((p) => ({ ...p, businessZip: e.target.value }))}
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-slate-400">Website URL (optional)</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                placeholder="https://"
                value={form.websiteUrl}
                onChange={(e) => setForm((p) => ({ ...p, websiteUrl: e.target.value }))}
              />
            </label>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={() =>
              void save({
                companyName: form.companyName,
                businessEmail: form.businessEmail,
                businessPhone: form.businessPhone,
                businessStreet: form.businessStreet,
                businessCity: form.businessCity,
                businessState: form.businessState,
                businessZip: form.businessZip,
                websiteUrl: form.websiteUrl,
              })
            }
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save business info"}
          </button>
        </section>

        {/* 2b White-label */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-lg font-semibold text-white">White-label branding</h2>
          <p className="mt-1 text-sm text-slate-400">Client portal login and dashboard use these colors, logo, and copy.</p>
          {tierIsStarter ? (
            <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">
              <span className="font-medium">Starter plan:</span> logo upload and URL are included. Custom colors, favicon,
              tagline, portal copy, and chatbot branding unlock on{" "}
              <Link to="/billing" className="font-semibold text-cyan-300 underline hover:text-cyan-200">
                Growth
              </Link>{" "}
              and above.
            </div>
          ) : null}
          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-2 lg:col-span-2">
              <span className="text-xs font-medium text-slate-400">Logo</span>
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-slate-700 bg-slate-950">
                  {logoPreview ? (
                    <img src={logoPreview} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-xs text-slate-600">No logo</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <input type="file" accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml" className="hidden" id="wl-logo" onChange={(e) => void handleLogo(e)} />
                  <label
                    htmlFor="wl-logo"
                    className="inline-block cursor-pointer rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 hover:bg-slate-700"
                  >
                    {uploadingLogo ? "Uploading…" : "Upload logo"}
                  </label>
                </div>
              </div>
              <input
                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100"
                placeholder="Or paste logo URL"
                value={form.logoUrl}
                onChange={(e) => setForm((p) => ({ ...p, logoUrl: e.target.value }))}
              />
            </div>
            <div className="space-y-2 lg:col-span-2">
              <span className="text-xs font-medium text-slate-400">Favicon</span>
              <div className="flex flex-wrap items-center gap-4">
                {form.faviconUrl ? (
                  <img src={form.faviconUrl.startsWith("http") ? form.faviconUrl : `${apiOrigin}${form.faviconUrl}`} alt="" className="h-8 w-8 object-contain" />
                ) : null}
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                  className="hidden"
                  id="wl-favicon"
                  disabled={tierIsStarter}
                  onChange={(e) => void handleFavicon(e)}
                />
                <label
                  htmlFor="wl-favicon"
                  className={`inline-block rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 ${
                    tierIsStarter
                      ? "pointer-events-none cursor-not-allowed opacity-50"
                      : "cursor-pointer hover:bg-slate-700"
                  }`}
                >
                  {uploadingFavicon ? "Uploading…" : "Upload favicon"}
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <span className="text-xs font-medium text-slate-400">Primary color</span>
              <div className="flex gap-2">
                <input
                  type="color"
                  disabled={tierIsStarter}
                  className="h-10 w-14 rounded border border-slate-600 bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                  value={/^#[0-9A-Fa-f]{6}$/.test(form.primaryColor) ? form.primaryColor : DEFAULT_PRIMARY}
                  onChange={(e) => setForm((p) => ({ ...p, primaryColor: e.target.value }))}
                />
                <input
                  disabled={tierIsStarter}
                  className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  value={form.primaryColor}
                  onChange={(e) => setForm((p) => ({ ...p, primaryColor: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <span className="text-xs font-medium text-slate-400">Accent color</span>
              <div className="flex gap-2">
                <input
                  type="color"
                  disabled={tierIsStarter}
                  className="h-10 w-14 rounded border border-slate-600 bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                  value={/^#[0-9A-Fa-f]{6}$/.test(form.accentColor) ? form.accentColor : DEFAULT_ACCENT}
                  onChange={(e) => setForm((p) => ({ ...p, accentColor: e.target.value }))}
                />
                <input
                  disabled={tierIsStarter}
                  className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  value={form.accentColor}
                  onChange={(e) => setForm((p) => ({ ...p, accentColor: e.target.value }))}
                />
              </div>
            </div>
            <label className="block lg:col-span-2">
              <span className="text-xs font-medium text-slate-400">Company tagline (login page)</span>
              <input
                disabled={tierIsStarter}
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Restoring your financial future"
                value={form.tagline}
                onChange={(e) => setForm((p) => ({ ...p, tagline: e.target.value }))}
              />
            </label>
            <label className="block lg:col-span-2">
              <span className="text-xs font-medium text-slate-400">Portal welcome message (dashboard)</span>
              <textarea
                disabled={tierIsStarter}
                className="mt-1 min-h-[88px] w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                value={form.portalWelcomeMessage}
                onChange={(e) => setForm((p) => ({ ...p, portalWelcomeMessage: e.target.value }))}
              />
            </label>
            <label className="block lg:col-span-2">
              <span className="text-xs font-medium text-slate-400">Chatbot name</span>
              <input
                disabled={tierIsStarter}
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                value={form.chatbotName}
                onChange={(e) => setForm((p) => ({ ...p, chatbotName: e.target.value }))}
              />
            </label>
            <label className="block lg:col-span-2">
              <span className="text-xs font-medium text-slate-400">Chatbot welcome message</span>
              <textarea
                disabled={tierIsStarter}
                className="mt-1 min-h-[72px] w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                value={form.chatbotWelcomeMessage}
                onChange={(e) => setForm((p) => ({ ...p, chatbotWelcomeMessage: e.target.value }))}
              />
            </label>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void openPortalPreview()}
              className="rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-700"
            >
              Preview client portal
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() =>
                void save({
                  logoUrl: form.logoUrl,
                  faviconUrl: form.faviconUrl,
                  primaryColor: form.primaryColor,
                  accentColor: form.accentColor,
                  tagline: form.tagline,
                  portalWelcomeMessage: form.portalWelcomeMessage,
                  chatbotName: form.chatbotName,
                  chatbotWelcomeMessage: form.chatbotWelcomeMessage,
                })
              }
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save branding"}
            </button>
          </div>
          <div className="mt-6 rounded-xl border border-slate-800 bg-slate-950/80 p-4">
            <p className="text-sm font-semibold text-slate-200">Quick preview</p>
            <div
              className="mt-4 overflow-hidden rounded-xl border border-slate-800 shadow-lg"
              style={{
                background: `linear-gradient(to bottom, ${form.primaryColor}22, #ffffff)`,
              }}
            >
              <div className="border-b border-slate-200/80 bg-white/95 px-4 py-3">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg text-xs font-bold text-white"
                    style={{ backgroundColor: form.primaryColor }}
                  >
                    {logoPreview ? <img src={logoPreview} alt="" className="max-h-full max-w-full object-contain" /> : "LOGO"}
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: form.primaryColor }}>
                      {form.companyName || "Your business"}
                    </p>
                    {form.tagline ? <p className="text-xs text-slate-500">{form.tagline}</p> : <p className="text-xs text-slate-500">Client portal</p>}
                  </div>
                </div>
              </div>
              <div className="bg-white px-4 py-5">
                <p className="text-sm font-medium text-slate-800">Welcome back</p>
                <button
                  type="button"
                  className="mt-4 w-full rounded-lg py-2.5 text-sm font-semibold text-white shadow-md"
                  style={{ backgroundColor: form.primaryColor, boxShadow: `0 8px 24px ${form.primaryColor}44` }}
                >
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* 2c Domain */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-lg font-semibold text-white">Subdomain &amp; custom domain</h2>
          <p className="mt-1 text-sm text-slate-400">Clients reach your portal on your branded host.</p>
          <div className="mt-4 space-y-3 text-sm">
            <div>
              <span className="text-slate-400">Current portal host</span>
              <p className="mt-1 font-mono text-slate-200">{form.portalSubdomainDisplay}</p>
            </div>
            <div>
              <span className="text-slate-400">Custom domain status</span>
              <p className="mt-1 text-slate-200">
                {form.customDomainStatus === "Active" && "Active"}
                {form.customDomainStatus === "PendingDNS" && "Pending DNS verification"}
                {form.customDomainStatus === "NotConfigured" && "Not configured"}
              </p>
            </div>
            <label className="block">
              <span className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-400">
                Custom domain
                {!tierIsAgency ? (
                  <span className="rounded-full bg-violet-500/25 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-200">
                    Agency plan required
                  </span>
                ) : null}
              </span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100 disabled:opacity-50"
                disabled={!form.customDomainEditable}
                placeholder="portal.yourbrand.com"
                value={form.customDomain}
                onChange={(e) => setForm((p) => ({ ...p, customDomain: e.target.value }))}
              />
            </label>
            {!form.customDomainEditable && (
              <p className="text-xs text-amber-200/90">Upgrade to the Agency plan to connect a custom domain.</p>
            )}
            <div className="rounded-lg border border-slate-700 bg-slate-950/60 p-3 text-xs text-slate-400">
              <p className="font-semibold text-slate-300">DNS setup</p>
              <p className="mt-1">Create a CNAME record pointing your subdomain to IntelliWarrior&apos;s portal endpoint. After changes propagate, mark the domain as verified in the admin console (coming soon).</p>
            </div>
          </div>
          {form.customDomainEditable && (
            <button
              type="button"
              disabled={saving}
              onClick={() => void save({ customDomain: form.customDomain })}
              className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save custom domain"}
            </button>
          )}
        </section>

        {/* 2d Email */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-lg font-semibold text-white">Email settings</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-slate-400">From name</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                placeholder={form.companyName || "Company name"}
                value={form.emailFromName}
                onChange={(e) => setForm((p) => ({ ...p, emailFromName: e.target.value }))}
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-slate-400">Reply-to email</span>
              <input
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.replyToEmail}
                onChange={(e) => setForm((p) => ({ ...p, replyToEmail: e.target.value }))}
              />
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs font-medium text-slate-400">Email footer</span>
              <textarea
                className="mt-1 min-h-[72px] w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.emailFooter}
                onChange={(e) => setForm((p) => ({ ...p, emailFooter: e.target.value }))}
              />
            </label>
            <p className="text-sm text-slate-400 md:col-span-2">
              SendGrid / provider:{" "}
              <span className={form.emailProviderStatus === "Connected" ? "text-emerald-400" : "text-amber-300"}>
                {form.emailProviderStatus === "Connected" ? "Connected" : "Not configured"}
              </span>
            </p>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={() =>
              void save({
                emailFromName: form.emailFromName,
                replyToEmail: form.replyToEmail,
                emailFooter: form.emailFooter,
              })
            }
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save email settings"}
          </button>
        </section>

        {/* Contract Signing (Phase 7.5) */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-lg font-semibold text-white">Contract Signing</h2>
          <p className="mt-1 text-sm text-slate-400">
            These names appear on the operator counter-signature of every CROA service agreement
            you send. Both fields are required before you can send a contract.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium text-slate-400">Operator legal name</span>
              <input
                type="text"
                maxLength={200}
                placeholder="e.g. Jane Q. Smith"
                value={form.operatorSignerLegalName}
                onChange={(e) =>
                  setForm((p) => ({ ...p, operatorSignerLegalName: e.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
              />
              <span className="mt-1 block text-xs text-slate-500">2–200 characters.</span>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-400">Operator title</span>
              <input
                type="text"
                maxLength={150}
                placeholder="e.g. Owner, Managing Director"
                value={form.operatorSignerTitle}
                onChange={(e) =>
                  setForm((p) => ({ ...p, operatorSignerTitle: e.target.value }))
                }
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
              />
              <span className="mt-1 block text-xs text-slate-500">2–150 characters.</span>
            </label>
            <p className="text-sm text-slate-400 md:col-span-2">
              Business legal name on contracts uses your{" "}
              <span className="font-medium text-slate-200">Company name</span>{" "}
              from Business information above.
            </p>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={() => void saveContractSigner()}
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save contract signer"}
          </button>
        </section>

        {/* 2e Notifications */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-lg font-semibold text-white">Notification preferences</h2>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.notifyEmailDisputeUpdates}
                onChange={(e) => setForm((p) => ({ ...p, notifyEmailDisputeUpdates: e.target.checked }))}
              />
              Email: dispute updates
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.notifyEmailNewClientSignups}
                onChange={(e) => setForm((p) => ({ ...p, notifyEmailNewClientSignups: e.target.checked }))}
              />
              Email: new client signups
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.notifyEmailBillingAlerts}
                onChange={(e) => setForm((p) => ({ ...p, notifyEmailBillingAlerts: e.target.checked }))}
              />
              Email: billing alerts
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.notifyInAppDisputeUpdates}
                onChange={(e) => setForm((p) => ({ ...p, notifyInAppDisputeUpdates: e.target.checked }))}
              />
              In-app: dispute updates
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.notifyInAppNewClientSignups}
                onChange={(e) => setForm((p) => ({ ...p, notifyInAppNewClientSignups: e.target.checked }))}
              />
              In-app: new clients
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.notifyInAppBillingAlerts}
                onChange={(e) => setForm((p) => ({ ...p, notifyInAppBillingAlerts: e.target.checked }))}
              />
              In-app: billing alerts
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.notifyClientDisputeStatus}
                onChange={(e) => setForm((p) => ({ ...p, notifyClientDisputeStatus: e.target.checked }))}
              />
              Clients: dispute status
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.notifyClientScoreUpdates}
                onChange={(e) => setForm((p) => ({ ...p, notifyClientScoreUpdates: e.target.checked }))}
              />
              Clients: score updates
            </label>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={() =>
              void save({
                notifyEmailDisputeUpdates: form.notifyEmailDisputeUpdates,
                notifyEmailNewClientSignups: form.notifyEmailNewClientSignups,
                notifyEmailBillingAlerts: form.notifyEmailBillingAlerts,
                notifyInAppDisputeUpdates: form.notifyInAppDisputeUpdates,
                notifyInAppNewClientSignups: form.notifyInAppNewClientSignups,
                notifyInAppBillingAlerts: form.notifyInAppBillingAlerts,
                notifyClientDisputeStatus: form.notifyClientDisputeStatus,
                notifyClientScoreUpdates: form.notifyClientScoreUpdates,
              })
            }
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save notifications"}
          </button>
        </section>

        {/* 2f Portal */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-lg font-semibold text-white">Portal settings</h2>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.clientSelfSignup}
                onChange={(e) => setForm((p) => ({ ...p, clientSelfSignup: e.target.checked }))}
              />
              Allow client self-signup
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.requireDocumentsOnSignup}
                onChange={(e) => setForm((p) => ({ ...p, requireDocumentsOnSignup: e.target.checked }))}
              />
              Require ID / documents on signup
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <input
                type="checkbox"
                className="rounded border-slate-600"
                checked={form.aiChatbotEnabled}
                onChange={(e) => setForm((p) => ({ ...p, aiChatbotEnabled: e.target.checked }))}
              />
              AI chatbot visible in client portal
            </label>
            <label className="block max-w-xs">
              <span className="text-xs font-medium text-slate-400">Default language</span>
              <select
                className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-slate-100"
                value={form.defaultPortalLanguage}
                onChange={(e) => setForm((p) => ({ ...p, defaultPortalLanguage: e.target.value }))}
              >
                <option value="en">English</option>
              </select>
            </label>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={() =>
              void save({
                clientSelfSignup: form.clientSelfSignup,
                requireDocumentsOnSignup: form.requireDocumentsOnSignup,
                aiChatbotEnabled: form.aiChatbotEnabled,
                defaultPortalLanguage: form.defaultPortalLanguage,
              })
            }
            className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save portal settings"}
          </button>
        </section>

        {/* Team & Integrations (existing placeholders) */}
        <section className="rounded-xl border border-slate-800 bg-slate-900 p-5">
          <h2 className="text-lg font-semibold text-white">Team &amp; integrations</h2>
          <p className="mt-1 text-sm text-slate-400">Use the Staff page for team members. Integration keys are managed per environment.</p>
        </section>

        {/* 2g Danger */}
        <section className="rounded-xl border border-red-900/50 bg-red-950/20 p-5">
          <h2 className="text-lg font-semibold text-red-200">Danger zone</h2>
          <p className="mt-1 text-sm text-red-200/80">Export your data before making irreversible changes.</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void exportData()}
              className="rounded-lg border border-slate-600 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800"
            >
              Export all data (JSON)
            </button>
          </div>
          <div className="mt-6 border-t border-red-900/40 pt-4">
            <p className="text-sm text-red-200/90">Request account deletion</p>
            <p className="mt-1 text-xs text-red-200/70">Type DELETE to confirm you understand this is a manual review process.</p>
            <input
              className="mt-2 w-full max-w-sm rounded-lg border border-red-900/50 bg-slate-900 px-3 py-2 text-slate-100"
              value={deletePhrase}
              onChange={(e) => setDeletePhrase(e.target.value)}
              placeholder="DELETE"
            />
            <button
              type="button"
              onClick={() => void requestDelete()}
              className="mt-3 rounded-lg bg-red-800 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              Submit deletion request
            </button>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
